import { type AnalyticsProperties, captureEvent, withAnalytics } from './analytics';

/**
 * Client-side error reporting, over the PostHog SDK analytics.ts already
 * loads. Nothing here assumes the SDK is present: no project key, a blocked
 * request or a failed init all leave every function below a no-op, which is
 * the state dev, test and Playwright run in.
 */

/** What an axios rejection carries besides the Error basics. `config.data` /
 * `response.data` hold the request and response BODIES — emails, phones,
 * billing payloads, registration answers. */
interface AxiosLikeError extends Error {
  code?: string;
  config?: { method?: string; url?: string; owns404?: boolean };
  response?: { status?: number; data?: { violation?: { field?: string; error?: string }; user_error?: boolean } };
}

/** Why a request ended without an answer, or null when it got one or never
 * was a request. Keyed on what every adapter sets rather than on messages:
 *
 * - `canceled` is the app withdrawing its own request (an AbortController
 *   signal, a screen unmounting) — CanceledError, code ERR_CANCELED.
 * - `timeout` is a `timeout` the caller configured running out. The fetch
 *   adapter and `clarifyTimeoutError` say ETIMEDOUT; the XHR adapter without it
 *   says ECONNABORTED with its "timeout … exceeded" message, the one case the
 *   code alone cannot tell from an abort.
 * - `connectivity` is the network or the browser dropping it: ERR_NETWORK, or
 *   ECONNABORTED "Request aborted", which the XHR adapter raises both for an
 *   abort event and for a status-0 exchange (a read still in flight when the
 *   page reloads). Any other rejection that carries a config and no response
 *   is the same story under a code the grouper would split out. */
export type UnansweredRequest = 'canceled' | 'timeout' | 'connectivity';

export function classifyUnansweredRequest(error: unknown): UnansweredRequest | null {
  const { name, code, message, config, response } = (error ?? {}) as AxiosLikeError;
  if (name === 'CanceledError' || code === 'ERR_CANCELED') return 'canceled';
  if (response != null) return null;
  if (code === 'ETIMEDOUT' || (code === 'ECONNABORTED' && /^timeout\b/.test(message ?? ''))) return 'timeout';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'connectivity';
  return config != null ? 'connectivity' : null;
}

/** A phone in a dead spot, a timeout or a screen unmounted mid-request
 * produces the same rejection as a crash, and there is nothing to fix in the app — reporting it
 * only buries the real crashes. */
export function isConnectivityFailure(error: unknown): boolean {
  return classifyUnansweredRequest(error) != null;
}

/** A 400 carrying a violation is the backend rejecting what someone typed, and
 * `user_error` is the same verdict on the bodies that carry no violation — a
 * declined card is the payer's bank saying no. Read from the raw error:
 * sanitizeError drops the body this lives in. */
function isUserInputViolation(error: unknown): boolean {
  const { response } = (error ?? {}) as AxiosLikeError;
  return response?.status === 400 && (response.data?.violation != null || response.data?.user_error === true);
}

/** A path segment that names a row rather than an endpoint. Collapsing these is
 * what makes every call of one route count as a single line instead of one per
 * league, team or game. */
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** The endpoint an axios URL names: ids collapsed to `:id`, query string gone —
 * a search term, an email or an invitation code is never part of the shape
 * being counted. Exported for the unit test. */
export function templatePath(url: string): string {
  return url
    .split(/[?#]/)[0]
    .split('/')
    .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/');
}

/**
 * The client errors this app ASKS its own API for, as the properties worth
 * counting — null for everything else. Each of these has a screen waiting for
 * it, so capturing one as an exception opens an issue and pages a human over a
 * feature working exactly as designed:
 *
 * - 400 carrying a violation (or `user_error`) is the backend refusing what
 *   someone typed, and the form paints the field they must fix.
 * - 401 is a session that is gone — the interceptor sends them to sign in — or
 *   the bot check refusing, which the page they are filling in explains.
 * - 403 is a refusal the interceptor announces before taking them home.
 * - 404 on a request whose caller set `owns404` renders its own not-found
 *   state — a stale link to a game on a soft-deleted league is the canonical
 *   case.
 *
 * Every other 4xx stays an exception, because nothing in the app planned for
 * it: a 400 with no violation is this app sending a body the backend cannot
 * read, a 404 no caller owns is a link to something that should be there, and
 * any other status is a rule nobody wrote down. Only our own API is judged —
 * an absolute URL is a third party whose 4xx no interceptor and no caller has
 * agreed to handle.
 *
 * The event carries the shape and nothing else: a path template, the status,
 * the method, and the violation's field and code. The request and response
 * bodies stay in the app, as they do on the exception path.
 */
export function buildApiClientEvent(context: string, error: unknown): AnalyticsProperties | null {
  const { config, response } = (error ?? {}) as AxiosLikeError;
  const { status } = response ?? {};
  const url = config?.url;
  if (status == null || url == null || !url.startsWith('/api')) return null;
  const expected = status === 401 || status === 403 || (status === 400 && isUserInputViolation(error)) || (status === 404 && config?.owns404 === true);
  if (!expected) return null;
  const properties: AnalyticsProperties = { context, status, path: templatePath(url) };
  if (config?.method) properties.method = config.method.toUpperCase();
  const violation = response?.data?.violation;
  if (violation?.field) properties.violation_field = violation.field;
  if (violation?.error) properties.violation_error = violation.error;
  return properties;
}

/** Rebuild the error from the fields error tracking needs to group and locate
 * it — name, message, stack — dropping everything else a serializer would
 * happily walk. Axios errors keep method/URL/status as explicit extras; their
 * request and response bodies never leave the app. Exported for the unit
 * test. */
export function sanitizeError(error: unknown): { error: Error; extra?: object } {
  const source = error instanceof Error ? error : new Error(String(error));
  const clean = new Error(source.message);
  clean.name = source.name;
  clean.stack = source.stack;
  const { config, response } = source as AxiosLikeError;
  const extra = config || response ? { method: config?.method, url: config?.url, status: response?.status } : undefined;
  return { error: clean, extra };
}

/** React reports recoverable errors — chiefly hydration mismatches on the
 * prerendered landing — through onRecoverableError. In production the message
 * is a minified "Minified React error #418" with no stack, so pull the code out
 * as a groupable property that names the mismatch kind (#418/#423/#425 …). */
export function parseReactErrorCode(error: unknown): string | undefined {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.match(/Minified React error #(\d+)/)?.[1];
}

export interface ExceptionReport {
  error: Error;
  properties: Record<string, unknown>;
}

/**
 * Which failures are worth an error tracking issue, as a pure function so the
 * accumulated knowledge about what is noise can be read and tested without an
 * SDK. Null means "do not capture as an exception" — for an expected client
 * outcome reportError counts an event instead.
 *
 * `$exception_level` is what PostHog ranks severity by. `extra` is merged last
 * on purpose: a caller that KNOWS its report is a handled degradation rather
 * than a crash passes its own `$exception_level` and wins.
 */
export function buildExceptionReport(context: string, error: unknown, extra?: Record<string, unknown>): ExceptionReport | null {
  // buildApiClientEvent owns the whole expected-outcome rule, so it is asked
  // here rather than restated: an outcome that is countable is never also an
  // issue, whichever of the two a caller reaches for.
  if (isConnectivityFailure(error) || buildApiClientEvent(context, error) != null) return null;
  const { error: clean, extra: axiosExtra } = sanitizeError(error);
  return { error: clean, properties: { context, $exception_level: 'error', ...axiosExtra, ...extra } };
}

/** Explicit report — for errors an in-app boundary swallowed before they could
 * reach the SDK's own uncaught handlers (the route errorElement, caught chunk
 * failures). `extra` adds diagnostic properties on the captured exception. */
export function reportError(context: string, error: unknown, extra?: Record<string, unknown>): void {
  try {
    // An outcome the app asked its own API for is counted, never captured: one
    // event keeps "how often does this endpoint refuse" answerable while the
    // issue list stays the list of what is broken. `extra` is the exception
    // path's diagnostic channel and has no place on a counted outcome.
    const event = buildApiClientEvent(context, error);
    if (event) {
      captureEvent('api_client_error', event);
      return;
    }
    const report = buildExceptionReport(context, error, extra);
    if (report) void withAnalytics((posthog) => posthog.captureException(report.error, report.properties)).catch(() => undefined);
  } catch {
    // Reporting must never take the app down with it, synchronously or as an
    // unhandled rejection once the SDK chunk settles.
  }
}
